import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { LockIcon, ViewIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Card, Heading, IconColor, Tooltip } from '@treams/treams-ui';

import { RevealFieldList } from './RevealFieldList';
import { RevealSubmission, useEvaluationSubmissionReveal } from './useEvaluationSubmissionReveal';

interface IProps {
	submission: RevealSubmission;
}

export function EvaluationSubmissionReveal({ submission }: IProps) {
	const { t } = useTranslation();

	const { reveal } = useEvaluationSubmissionReveal(submission);

	const isEmployee = submission.viewer.party === graphql.EvaluationSubmissionParty.Employee;

	// The backend already returns null for employees while the reveal is not
	// visible to them; no client-side visibility check needed.
	if (!reveal) {
		return null;
	}

	return (
		<Flex justifyContent="flex-end">
			<Box mb={4} width={[1, 6 / 12]} minWidth={320}>
				<Card>
					<Flex justifyContent="space-between" alignItems="center" mb={3}>
						<Heading>
							{t(
								isEmployee
									? 'conversations.reveal.titleEmployee'
									: reveal.visibleToEmployee
										? 'conversations.reveal.titleManager'
										: 'conversations.reveal.titleManagerNotShared',
								{ firstName: submission.employee.user.firstName },
							)}
						</Heading>
						{!isEmployee &&
							(reveal.visibleToEmployee ? (
								<Tooltip
									content={t('conversations.reveal.visibleTooltip', {
										firstName: submission.employee.user.firstName,
										lastName: submission.employee.user.lastName,
									})}
									position="top"
									portal
								>
									<IconColor color="slateBlue">
										<ViewIcon width={20} height={20} aria-hidden />
									</IconColor>
								</Tooltip>
							) : (
								<Tooltip content={t('conversations.reveal.lockedTooltip')} position="top" portal>
									<IconColor color="slateBlue">
										<LockIcon width={20} height={20} aria-hidden />
									</IconColor>
								</Tooltip>
							))}
					</Flex>
					<RevealFieldList fields={reveal.fields} />
				</Card>
			</Box>
		</Flex>
	);
}
